import { useEffect, useRef } from "react";

const loadScript = (src, ready) =>
  new Promise((resolve) => {
    if (ready()) return resolve();
    if (!document.querySelector(`script[src="${src}"]`)) {
      const el = document.createElement("script");
      el.src = src;
      el.async = true;
      document.head.appendChild(el);
    }
    const t = setInterval(() => {
      if (ready()) {
        clearInterval(t);
        resolve();
      }
    }, 150);
  });

export const youtubeSrc = (channel, muted) => {
  const ch = encodeURIComponent(channel);
  const base = channel.startsWith("UC")
    ? `https://www.youtube.com/embed/live_stream?channel=${ch}`
    : `https://www.youtube.com/embed/${ch}`;
  return `${base}&autoplay=1&mute=${muted ? 1 : 0}&enablejsapi=1&origin=${window.location.origin}`;
};

export const kickSrc = (channel, muted) =>
  `https://player.kick.com/${encodeURIComponent(channel)}?muted=${muted}&autoplay=true&playsinline=true&parent=${window.location.hostname}`;

// Twitch: real player API, so we get PLAY/PAUSE and can mute without reloading
const TwitchPlayer = ({ s, muted, onPlaying, onOffline, testId }) => {
  const id = `twitch-${s.id}`;
  const ref = useRef(null);

  useEffect(() => {
    let dead = false;
    loadScript("https://embed.twitch.tv/embed/v1.js", () => window.Twitch?.Player).then(() => {
      if (dead) return;
      const P = window.Twitch.Player;
      const player = new P(id, {
        channel: s.channel,
        parent: [window.location.hostname],
        muted: true,
        autoplay: true,
        width: "100%",
        height: "100%",
      });
      ref.current = player;
      player.addEventListener(P.PLAYING, () => onPlaying(true));
      player.addEventListener(P.PLAY, () => onPlaying(true));
      player.addEventListener(P.PAUSE, () => onPlaying(false));
      player.addEventListener(P.ENDED, () => onOffline());
      player.addEventListener(P.OFFLINE, () => onOffline());
    });
    return () => {
      dead = true;
      onPlaying(true);
      try {
        ref.current?.destroy?.();
      } catch {
        /* player already gone */
      }
      ref.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.id, s.channel]);

  useEffect(() => {
    try {
      ref.current?.setMuted(muted);
      if (!muted) ref.current?.setVolume?.(0.7);
    } catch {
      /* not ready yet */
    }
  }, [muted]);

  return <div id={id} className="absolute inset-0 h-full w-full" data-testid={testId} />;
};

// YouTube: attach the iframe API to our own iframe for state + mute control
const YouTubePlayer = ({ s, muted, onPlaying, onOffline, testId }) => {
  const ref = useRef(null);
  const player = useRef(null);

  useEffect(() => {
    let dead = false;
    loadScript("https://www.youtube.com/iframe_api", () => window.YT?.Player).then(() => {
      if (dead || !ref.current) return;
      player.current = new window.YT.Player(ref.current, {
        events: {
          onStateChange: (e) => {
            if (e.data === 0) return onOffline(); // stream ended
            onPlaying(e.data === 1 || e.data === 3);
          },
          onError: () => onOffline(),
        },
      });
    });
    return () => {
      dead = true;
      onPlaying(true);
      player.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.id, s.channel]);

  useEffect(() => {
    const p = player.current;
    if (!p?.mute) return;
    try {
      muted ? p.mute() : p.unMute();
    } catch {
      /* not ready yet */
    }
  }, [muted]);

  return (
    <iframe
      ref={ref}
      title={s.creator_name}
      src={youtubeSrc(s.channel, true)}
      className="absolute inset-0 h-full w-full"
      allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
      allowFullScreen
      frameBorder="0"
      data-testid={testId}
    />
  );
};

// Kick has no player API: mute by reloading the iframe, playback state unknowable
const KickPlayer = ({ s, muted, testId }) => (
  <iframe
    key={muted ? "muted" : "sound"}
    title={s.creator_name}
    src={kickSrc(s.channel, muted)}
    className="absolute inset-0 h-full w-full"
    allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
    allowFullScreen
    frameBorder="0"
    scrolling="no"
    data-testid={testId}
  />
);

export const StreamPlayer = ({ s, muted, onPlaying, onOffline }) => {
  const testId = `stream-frame-${s.id}`;
  if (s.platform === "twitch")
    return <TwitchPlayer s={s} muted={muted} onPlaying={onPlaying} onOffline={onOffline}
      testId={testId} />;
  if (s.platform === "youtube")
    return <YouTubePlayer s={s} muted={muted} onPlaying={onPlaying} onOffline={onOffline}
      testId={testId} />;
  return <KickPlayer s={s} muted={muted} testId={testId} />;
};
